import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { InvoiceStatus } from '@frontend/api-invoices';
import { PersonsV3, PersonTypes } from '@frontend/api-person';
import { AdaptoActions } from '@frontend/adapto-actions';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  CollectPaymentMulti,
  CollectPaymentMultiFlow,
  CollectPaymentMultiModalInstance,
  useCollectPaymentMulti,
  PAYMENT_MULTI_STEPS,
  useSyncCollectPaymentMultiToModal,
  CollectPaymentMultiModal,
  CollectPaymentMultiPortal,
  usePaymentsModalButton,
} from '@frontend/payments-collection-flow';
import { REQUEST_ORIGINS } from '@frontend/person-invoice-create';
import { usePersonActiveInvoiceList } from '@frontend/person-invoice-list';
import { ContactActionPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  ActionButton,
  Button,
  PopoverMenu,
  PopoverMenuItemDropdown,
  useNestedPopoverMenu,
  useModalControl,
  ModalControlModalProps,
  ModalControlTriggerProps,
  useTooltip,
} from '@frontend/design-system';
import { ContactMenuItem } from './contact-menu-item';
import { InvoicePopoverMenuItems } from './invoice-popover-menu-items';

export type PaymentButtonProps = {
  label?: string;
  context: {
    person?: PersonTypes.Person;
    personId: string;
    locationId: string;
  };
  trackingIdSuffix?: string;
  origin?: REQUEST_ORIGINS;
};

export const PaymentButton = ({ label, context, trackingIdSuffix, origin }: PaymentButtonProps) => {
  const { triggerProps, modalProps } = useModalControl();
  return (
    <div>
      <ActionButton
        {...triggerProps}
        trackingId={`${ContactActionPrefixes.Payments}:${trackingIdSuffix}`}
        label={label}
      >
        <Icon name='dollar-sign' size={16} />
      </ActionButton>
      <MultiCollectModal
        modalProps={modalProps}
        personId={context.personId}
        locationId={context.locationId}
        origin={origin}
      />
    </div>
  );
};

const MultiCollectModal = ({
  modalProps,
  invoiceId,
  personId,
  locationId,
  flow,
  origin,
}: {
  modalProps: ModalControlModalProps;
  invoiceId?: string;
  personId?: string;
  locationId?: string;
  flow?: CollectPaymentMultiFlow;
  origin?: REQUEST_ORIGINS;
}) => {
  return (
    <CollectPaymentMultiModalInstance
      modalProps={modalProps}
      collectProps={{
        personId,
        invoiceId,
        locationId,
        flow: flow || (personId && !invoiceId ? 'select' : invoiceId ? 'pay' : 'create'),
        origin,
      }}
    />
  );
};

export type PaymentActionProps = {
  label: string;
  context: {
    personId: string;
    locationId: string;
  };
  trackingIdSuffix?: string;
  origin?: REQUEST_ORIGINS;
};

export const PaymentAction = ({ label, context, trackingIdSuffix, origin }: PaymentActionProps) => {
  const { Tooltip, triggerProps, modalProps } = usePaymentsModalButton({
    personId: context.personId,
  });

  return (
    <>
      <AdaptoActions.Action
        {...triggerProps}
        trackingId={`${ContactActionPrefixes.Payments}:${trackingIdSuffix}`}
        icon='dollar-sign'
        label={label}
      />
      {Tooltip}
      <MultiCollectModal
        modalProps={modalProps}
        personId={context.personId}
        locationId={context.locationId}
        origin={origin}
      />
    </>
  );
};

export interface PaymentHighlightedActionProps extends PaymentActionProps {
  showPulseAnimation?: boolean;
  trackingId?: string;
  triggerProps?: ModalControlTriggerProps;
}

export const PaymentHighlightedAction = ({
  trackingId,
  trackingIdSuffix,
  context,
  showPulseAnimation,
  label,
  origin,
}: PaymentHighlightedActionProps) => {
  const { Tooltip, triggerProps, modalProps } = usePaymentsModalButton({
    personId: context.personId,
  });
  return (
    <>
      <AdaptoActions.Action
        {...triggerProps}
        trackingId={trackingId || `${ContactActionPrefixes.Payments}:${trackingIdSuffix}`}
        icon='pay'
        label={label}
        css={[highlightedActionStyles, showPulseAnimation && pulseAnimationStyles]}
      />
      {Tooltip}
      <MultiCollectModal
        modalProps={modalProps}
        personId={context.personId}
        locationId={context.locationId}
        origin={origin}
      />
    </>
  );
};

const PAYMENT_SUCCESS_TIMEOUT = 2000;

export type ContactPaymentActionProps = {
  context: {
    person?: PersonTypes.Person;
    personId: string;
    locationId: string;
    associatedPersonIds?: string[];
  };
  trackingIdSuffix?: string;
  isCompactView?: boolean;
  /** Show only '$' with the formatted amount in a hover tooltip, without changing button styling. */
  collapseToSymbol?: boolean;
};
export const ContactPaymentAction = ({
  context: { personId, locationId, associatedPersonIds },
  trackingIdSuffix,
  isCompactView,
  collapseToSymbol,
}: ContactPaymentActionProps) => {
  const { t } = useTranslation('contacts');
  /**
   * For manual card entry, 2s isn't quiiiiittteee enough.
   * So, either change to a 3s or 4s timeout for everything, or have an optional check to query a 2nd time, if needed.
   */
  const smartRefetchOnTimeout = (attempts = 0) => {
    if (attempts > 1) {
      return;
    }

    setTimeout(async () => {
      const currentInvoiceIdsStr = invoices?.map((invoice) => invoice.id).join(',');

      const refetchedResults = await refetchInvoices();
      const updatedInvoiceIdsStr = refetchedResults
        .flatMap((query) => query?.data?.invoices || [])
        .map((invoice) => invoice.id)
        .join(',');

      if (currentInvoiceIdsStr === updatedInvoiceIdsStr) {
        smartRefetchOnTimeout(attempts + 1);
      }
    }, PAYMENT_SUCCESS_TIMEOUT);
  };

  const { invoices, refetchInvoices, multiPersonGroupedInvoices, isFetchInvoicesLoading } = usePersonActiveInvoiceList(
    personId,
    associatedPersonIds
  );

  const [selectedPersonId, setSelectedPersonId] = useState(personId);

  useEffect(() => {
    /**
     * Change selected person if prop changes
     */
    setSelectedPersonId(personId);
  }, [personId]);

  const {
    triggerProps: paymentTriggerProps,
    modalProps: paymentModalProps,
    closeModal: closePaymentModal,
  } = useModalControl();

  const {
    getTriggerProps: getMenuTriggerProps,
    getMenuProps,
    getItemProps: getMenuItemProps,
    close: closeMenu,
  } = useNestedPopoverMenu({
    placement: 'bottom-start',
  });

  const multiCollectProps = useCollectPaymentMulti(
    {
      /**
       * If coming from a multi-select location where
       * locationId is important and/or connected to the person
       * such as in ContactPanel, use that locationId here.
       */
      locationId,
      trackingId: trackingIdSuffix,
      personId: selectedPersonId,
      onPaymentSuccess: () => {
        smartRefetchOnTimeout();
      },
      onInvoiceCreated: () => {
        smartRefetchOnTimeout();
      },
    },
    {
      trackingId: trackingIdSuffix,
      onCancel: () => {
        closePaymentModal();
        multiCollectProps.resetState();
      },
      onComplete: () => {
        closePaymentModal();
        multiCollectProps.resetState();
      },
    }
  );

  const { person: legacyPerson, setFlowAndStep, setActiveInvoiceId } = multiCollectProps;

  const { disableCloseOnEscape, disableCloseOnOverlayClick } = useSyncCollectPaymentMultiToModal({
    activeStep: multiCollectProps.multiStep.activeStep,
    setShowCancelAction: multiCollectProps.multiStep.setShowCancelAction,
  });

  const person = PersonsV3.PersonHelpers.convertPersonV3ToPerson(legacyPerson);

  const onClickCreateNewInvoice = useCallback(() => {
    setActiveInvoiceId(null);
    setFlowAndStep('create', PAYMENT_MULTI_STEPS.create);
    closeMenu();
    paymentTriggerProps.onClick();
  }, [paymentTriggerProps.onClick]);

  const onClickCollectPaymentFromInvoice = useCallback(
    (id: string) => {
      setActiveInvoiceId(id);
      setFlowAndStep('pay', PAYMENT_MULTI_STEPS.paymentMethods);
      closeMenu();
      paymentTriggerProps.onClick();
    },
    [paymentTriggerProps.onClick, closeMenu]
  );

  const onClickSendReminder = useCallback(
    (id: string) => {
      setActiveInvoiceId(id);
      setFlowAndStep('pay', PAYMENT_MULTI_STEPS.shareInMessage);
      closeMenu();
      paymentTriggerProps.onClick();
    },
    [paymentTriggerProps.onClick, closeMenu]
  );

  const { beforeUnmountCallback, resetState } = multiCollectProps;

  const onCloseModalDirectly = useCallback(() => {
    closePaymentModal();
    beforeUnmountCallback();
    resetState();
  }, [closePaymentModal, beforeUnmountCallback, resetState]);

  const personIdsWithInvoices = Object.keys(multiPersonGroupedInvoices ?? {});

  const totalOutstandingAmount = useMemo(() => {
    if (!invoices || !invoices.length) {
      return 0;
    }

    return invoices.reduce((acc, invoice) => {
      if (invoice.status !== InvoiceStatus.Unpaid) {
        return acc;
      }

      return acc + invoice.billedAmount;
    }, 0);
  }, [invoices, person]);

  const formattedAmount = useMemo(() => {
    return formatCentsToCurrency(totalOutstandingAmount, false);
  }, [totalOutstandingAmount]);

  const {
    disabled,
    disabledMessage,
    tooltipTriggerProps: disableTooltipTriggerProps,
    Tooltip: DisabledTooltip,
  } = usePaymentsModalButton({
    personId: person.PersonID,
  });
  const {
    Tooltip: AmountTooltip,
    triggerProps: amountTooltipTriggerProps,
    tooltipProps: amountTooltipProps,
  } = useTooltip({ placement: 'bottom', hoverDelay: 300 });

  const getLabel = () => {
    return disabled ? disabledMessage : totalOutstandingAmount > 0 ? t('Unpaid Requests') : t('Create Payment Request');
  };

  const computedLabel = getLabel();
  const shouldShowAmountTooltip = (isCompactView || collapseToSymbol) && totalOutstandingAmount > 0 && !disabled;
  const tooltipTriggerProps = shouldShowAmountTooltip ? amountTooltipTriggerProps : disableTooltipTriggerProps;

  return (
    <>
      {isFetchInvoicesLoading && !isCompactView ? (
        <Button
          variant='secondary'
          iconName='pay'
          className='payment-action-button'
          css={unPaidRequestButtonStyle(disabled)}
          loading={true}
        />
      ) : totalOutstandingAmount ? (
        <div style={{ position: 'relative' }} {...tooltipTriggerProps}>
          <Button
            trackingId={`${ContactActionPrefixes.Payments}:${trackingIdSuffix}:unpaid--trigger`}
            variant='secondary'
            {...(shouldShowAmountTooltip && { iconName: 'pay' })}
            className='payment-action-button'
            disabled={disabled}
            {...getMenuTriggerProps()}
            css={[
              unPaidRequestButtonStyle(disabled),
              collapseToSymbol &&
                css`
                  span {
                    min-width: unset;
                    gap: 0;
                  }
                `,
              isCompactView &&
                css`
                  border: none;
                  background: none;
                  color: #9d6b02;
                  font-weight: ${theme.font.weight.regular};
                  padding: 0;
                  &:hover {
                    background: none;
                  }
                `,
            ]}
            loading={isFetchInvoicesLoading}
          >
            {isCompactView || collapseToSymbol ? '$' : formattedAmount}
          </Button>
          {shouldShowAmountTooltip ? (
            <AmountTooltip {...amountTooltipProps}>{formattedAmount}</AmountTooltip>
          ) : (
            DisabledTooltip
          )}
          <PopoverMenu {...getMenuProps()}>
            {!multiPersonGroupedInvoices ? (
              <InvoicePopoverMenuItems
                invoices={invoices}
                onClickSendReminder={onClickSendReminder}
                onClickCollectPaymentFromInvoice={onClickCollectPaymentFromInvoice}
                onClickCreateNewInvoice={onClickCreateNewInvoice}
                getMenuItemProps={getMenuItemProps}
                trackingIdSuffix={trackingIdSuffix}
              />
            ) : (
              <div css={{ overflowY: 'auto', maxHeight: '290px', width: '100%' }}>
                {personIdsWithInvoices.map((personId, index) => {
                  const currentInvoices = multiPersonGroupedInvoices[personId] ?? [];
                  return (
                    <PopoverMenuItemDropdown
                      index={index}
                      key={personId}
                      trackingId={`${ContactActionPrefixes.Payments}:${trackingIdSuffix}`}
                      css={{
                        width: '100%',
                        height: theme.spacing(6),
                        cursor: 'pointer',
                        '& svg': {
                          width: theme.spacing(2),
                        },
                      }}
                      subMenuItems={() => (
                        <InvoicePopoverMenuItems
                          invoices={currentInvoices}
                          onClickSendReminder={onClickSendReminder}
                          onClickCollectPaymentFromInvoice={onClickCollectPaymentFromInvoice}
                          onClickCreateNewInvoice={onClickCreateNewInvoice}
                          getMenuItemProps={getMenuItemProps}
                          trackingIdSuffix={trackingIdSuffix}
                        />
                      )}
                      triggerProps={{
                        onClick: () => setSelectedPersonId(personId),
                      }}
                    >
                      <ContactMenuItem
                        name={currentInvoices?.[0]?.person.name ?? ''}
                        locationId={locationId}
                        personId={personId}
                        index={index}
                        invoiceCount={currentInvoices?.length}
                      />
                    </PopoverMenuItemDropdown>
                  );
                })}
              </div>
            )}
          </PopoverMenu>
        </div>
      ) : (
        <AdaptoActions.Action
          {...paymentTriggerProps}
          onClick={onClickCreateNewInvoice}
          trackingId={`${ContactActionPrefixes.Payments}:${trackingIdSuffix}`}
          className='payment-action-button'
          icon='dollar-sign'
          label={computedLabel}
          disabled={disabled}
          css={isCompactView && { border: 'none', height: '16px', width: '16px' }}
        />
      )}
      <CollectPaymentMultiModal
        {...paymentModalProps}
        onClose={onCloseModalDirectly}
        disableCloseOnEscape={disableCloseOnEscape}
        disableCloseOnOverlayClick={disableCloseOnOverlayClick}
        minWidth={multiCollectProps.minWidth}
      >
        <CollectPaymentMulti {...multiCollectProps} origin={REQUEST_ORIGINS.CONTACT_PANEL_SHORTCUT} />
      </CollectPaymentMultiModal>
      <CollectPaymentMultiPortal multiControl={multiCollectProps} locationId={locationId} />
    </>
  );
};

const pulseAnimationStyles = css({
  ':after': {
    animation: 'pulse 2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 0,
    },
    '50%': {
      opacity: 1,
    },
  },
});

const highlightedActionStyles = css({
  color: theme.colors.tangerine40,
  span: {
    color: theme.colors.tangerine40,
  },
  svg: {
    color: theme.colors.tangerine40,
  },
  ':after': {
    content: '""',
    position: 'absolute',
    inset: 4,
    borderRadius: theme.borderRadius.medium,
    backgroundColor: theme.colors.tangerine10,
    zIndex: -1,
  },
});

const unPaidRequestButtonStyle = (isDisabled = false) => css`
  line-height: 1;
  background-color: ${theme.colors.tangerine5};
  border-color: ${theme.colors.warning30};
  color: ${theme.colors.neutral90};

  ${!isDisabled &&
  `svg {
    fill: ${theme.colors.warning40};
    color: ${theme.colors.warning40};
  }

  &:hover,
  &:focus {
    background-color: ${theme.colors.tangerine10};
  }`}
`;
